import { useCallback, useEffect, useRef, useState } from 'react'
import Service from './service'
import { createUploadQueue, isActive } from './uploadQueue'
import { stageError } from './errors'
import { STAGE_CONCURRENCY } from './constants'

// Upload progress (5% steps of up to three files at once, over hundreds of
// files) reaches React at most this often; a change of state goes at once.
const PROGRESS_MS = 200

/**
 * The upload queue of one batch's file grid, as React state. Every file is
 * one POST /:batchId/rows/:rowId/files/:kind, STAGE_CONCURRENCY at a time.
 *
 *   onSaved(item, data)  one file saved; data = the row's files/status/missing
 *   onFatal(failure)     the queue stopped (batch closed or gone, permission
 *                        or session lost): the rest is left unsent
 *   onDrain(summary)     nothing left in flight: { saved, failed, fatal }
 *
 * Leaving the page stops the queue: nothing new starts; uploads already on the
 * wire are not aborted (they finish on the server, and each saved file is a
 * complete stage of its row).
 */
export default function useStagingQueue({ batchId, onSaved, onFatal, onDrain }) {
  const [items, setItems] = useState([])
  const mounted = useRef(true)
  const listeners = useRef({ onSaved, onFatal, onDrain })
  listeners.current = { onSaved, onFatal, onDrain }

  // The latest snapshot of the queue, handed to React now (a state changed)
  // or at the next PROGRESS_MS tick (only progress moved).
  const latest = useRef(null)
  const timer = useRef(null)
  const publish = useRef(() => {})
  publish.current = () => {
    clearTimeout(timer.current)
    timer.current = null
    if (mounted.current && latest.current) setItems(latest.current)
  }

  const queueRef = useRef(null)
  if (queueRef.current === null) {
    queueRef.current = createUploadQueue({
      concurrency: STAGE_CONCURRENCY,
      upload: (item, onProgress) =>
        Service.stageFile(batchId, item.rowId, item.kind, item.file, {
          typeFix: item.typeFix,
          onUploadProgress: (e) => {
            const fraction = e?.progress ?? (e?.total ? e.loaded / e.total : null)
            if (fraction !== null && fraction !== undefined) onProgress(fraction)
          },
        }).then((res) => res?.data),
      classify: stageError,
      onChange: (snapshot, { progress = false } = {}) => {
        latest.current = snapshot
        if (!progress) publish.current()
        else if (!timer.current) timer.current = setTimeout(() => publish.current(), PROGRESS_MS)
      },
      onSaved: (item, data) => mounted.current && listeners.current.onSaved?.(item, data),
      onFatal: (failure, item) => mounted.current && listeners.current.onFatal?.(failure, item),
      onDrain: (summary) => mounted.current && listeners.current.onDrain?.(summary),
    })
  }

  useEffect(() => {
    mounted.current = true
    const queue = queueRef.current
    return () => {
      mounted.current = false
      clearTimeout(timer.current)
      timer.current = null
      queue.stop()
    }
  }, [])

  /** Send these files (entries of the grid: { id, rowId, rowNo, kind, file, typeFix }). */
  const save = useCallback((entries) => {
    const queue = queueRef.current
    queue.clearFinished()
    queue.add(
      entries.map(({ id, rowId, rowNo, kind, file, typeFix }) => ({ id, rowId, rowNo, kind, file, typeFix }))
    )
  }, [])
  const retry = useCallback((id) => queueRef.current.retry(id), [])
  const retryFailed = useCallback(() => queueRef.current.retryFailed(), [])
  const forget = useCallback((id) => queueRef.current.remove(id), [])
  const clearFinished = useCallback(() => queueRef.current.clearFinished(), [])

  return {
    items,
    running: items.some(isActive),
    save,
    retry,
    retryFailed,
    forget,
    clearFinished,
  }
}
